<script lang="ts">
import type { ModelModifiers, TextareaEmits, TextareaSlots, TextareaProps as UTextareaProps } from '#ui/types';

type TextareaValue = string | number;

export interface TextareaProps<T extends TextareaValue = TextareaValue, Mod extends ModelModifiers = ModelModifiers> extends UTextareaProps<T, Mod> {
	/**
	 * Take full width
	 */
	block?: boolean;
	/**
	 * Show copy button
	 */
	copy?: boolean;
	/**
	 * Make the textarea readonly
	 */
	readonly?: boolean;
}
</script>

<script setup lang="ts" generic="T extends TextareaValue = TextareaValue, Mod extends ModelModifiers = ModelModifiers">
import Textarea from '@nuxt/ui/components/Textarea.vue';
import { useForwardExpose, useForwardPropsEmits } from 'reka-ui';
import { computed } from 'vue';
import { isNil } from '#layers/base/public';
import { mergeUi } from '#layers/ui/utils/merge-ui';

const { ui: uiProp, copy, size, class: className, ...forwardedProps } = defineProps<TextareaProps<T, Mod>>();
const emit = defineEmits<TextareaEmits<T, Mod>>();
const slots = defineSlots<TextareaSlots>();

const forward = useForwardPropsEmits(() => ({ ...forwardedProps, ui: uiProp, size, class: className }), emit);
const { forwardRef } = useForwardExpose();

const ui = computed(() => mergeUi(uiProp, {
	trailing: copy ? 'flex flex-col items-center w-10 px-0!' : '',
}));

const copyButtonSize = computed(() => size === 'lg' || size === 'xl' || size == null ? 'md' : size);
</script>

<template>
	<!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -- wraps Nuxt UI Textarea; the consuming form field supplies the label -->
	<Textarea
		:ref="forwardRef"
		v-bind="forward"
		:class="[className, block ? 'w-full' : '']"
		:ui
	>
		<template v-for="(_, name) in slots" #[name]="slotData: any">
			<slot :name v-bind="slotData" />
		</template>

		<template v-if="copy && !isNil(modelValue)" #trailing>
			<DCopyButton :value="String(modelValue)" :size="copyButtonSize" />
		</template>
	</Textarea>
</template>
